/**
 * @file index.tsx
 * @module app/modules/sales-management/admin/notifications/index
 * @description Implements the index interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorIcon from "../../../../../_admin/assets/media/critical-errors.png";
import APICallService from "../../../../../api/apiCallService";
import { AUTH } from "../../../../../api/apiEndPoints";
import Loader from "../../../../../Global/loader";
import Pagination from "../../../../../Global/pagination";
import { InActiveCustomerAlerts, LostOrderAlerts, PAGE_LIMIT } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { CustomSelectWhite } from "../../../../custom/Select/CustomSelectWhite";
import { useAuth } from "../../../auth";
import ImageWithFallback from "../../../_common/components/image-with-fallback";

const platformOptions = [
  {
    label: "Inactive Customer Alerts",
    value: InActiveCustomerAlerts,
    title: "Inactive Customer Alerts",
  },
  {
    label: "Lost Order Alerts",
    value: LostOrderAlerts,
    title: "Lost Order Alerts",
  },
];

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { state }: any = useLocation();

  let currentOptions = platformOptions;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [totalRecords, setTotalRecords] = useState(-1);
  const [logsList, setLogsList] = useState<any>([]);
  const [selectedNotification, setSelectedNotification] = useState<any>(
    state && state?.type
      ? currentOptions.find((item: any) => item.value == state?.type)
      : null
  );

  useEffect(() => {
    (async () => {
      await Promise.all([
        fetchProducts(page, pageLimit, selectedNotification?.value || ""),
      ]);
    })();
  }, []);

  const fetchProducts = async (
    page: number,
    limit: number,
    notificationType?: any
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      needCount: true,
    };
    if (notificationType) {
      params["type[0]"] = notificationType;
    } else {
      params["type[0]"] = InActiveCustomerAlerts;
      params["type[1]"] = LostOrderAlerts;
    }
    const apiCallService = new APICallService(
      AUTH.notification,
      params,
      "",
      "",
      "",
      ""
    );
    const response = await apiCallService.callAPI();
    if (response) {
      setLogsList(response?.records || []);
      if (page == 1) {
        setTotalRecords(response?.total || 0);
      }
    }
    setLoading(false);
  };

  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setPage(val);
    await fetchProducts(val, pageLimit, selectedNotification?.value || "");
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    await fetchProducts(val + 1, pageLimit, selectedNotification?.value || "");
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    await fetchProducts(val - 1, pageLimit, selectedNotification?.value || "");
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(+event.target.value);
    await fetchProducts(
      1,
      event.target.value,
      selectedNotification?.value || ""
    );
  };

  const handleShipementChange = async (event: any) => {
    setSelectedNotification(event);
    setPage(1);
    await fetchProducts(1, pageLimit, event?.value || "");
  };

  return (
    <>
      <div className="p-5">
        <Row className="g-5">
          <Col xs={12}>
            <h1 className="fs-22 fw-bolder">Notifications</h1>
          </Col>
          <Col xs={12}>
            <Card className="bg-light border">
              <Card.Body className="px-7 py-6">
                <Row className="align-items-center gy-5">
                  <Col sm={12} md={6} lg={4}>
                    <Form.Label className="fs-16 fw-500 text-dark me-5 mb-0">
                      Select Notification Type
                    </Form.Label>
                    <div className="min-w-300px mt-3">
                      <CustomSelectWhite
                        placeholder="Select platform"
                        isSearchable={true}
                        isClearable={true}
                        options={currentOptions}
                        value={selectedNotification}
                        onChange={(opt: any) => handleShipementChange(opt)}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <Card className="border border-r10px">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                        <th className="min-w-250px">Message</th>
                        <th className="min-w-100x">Created At</th>
                        {/* <th className="min-w-100px"></th> */}
                      </tr>
                    </thead>
                    <tbody>
                      <>
                        {loading ? (
                          <>
                            <tr>
                              <td colSpan={3}>
                                <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                  <Loader loading={loading} />
                                </div>
                              </td>
                            </tr>
                          </>
                        ) : (
                          <>
                            {logsList.length ? (
                              <>
                                {logsList.map(
                                  (productVal: any, index: number) => {
                                    return (
                                      <>
                                        <tr
                                          className="h-70px"
                                          onClick={() => {
                                            if (
                                              productVal?.metadata?.variantId
                                            ) {
                                              navigate(
                                                "/products/master-products/view-product-details",
                                                {
                                                  state:
                                                    productVal?.metadata
                                                      ?.variantId || "",
                                                }
                                              );
                                            }
                                          }}
                                        >
                                          <td>
                                            <div className="d-flex gap-3 align-items-center">
                                              <ImageWithFallback
                                                src={
                                                  productVal?.type == 4
                                                    ? ErrorIcon
                                                    : productVal?.image || ""
                                                }
                                                height={35}
                                                width={35}
                                                alt="icon"
                                              />
                                              <div className="fs-16 fw-500">
                                                {productVal?.message || ""}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="text-start justify-content-between align-items-center">
                                            <div className="fs-16 fw-500">
                                              {productVal?._createdAt
                                                ? Method.convertDateToDDMMYYYYHHMMAMPMWithSeperator(
                                                  productVal?._createdAt,
                                                  " - "
                                                )
                                                : "-"}
                                            </div>
                                          </td>

                                          {/* <td className="text-end">
                                            <Button
                                              variant="primary"
                                              className="text-nowrap"
                                              onClick={() => {
                                                setSelectedLog(productVal);
                                                setShowModal(true);
                                              }}
                                            >
                                              View Message
                                            </Button>
                                          </td> */}
                                        </tr>
                                      </>
                                    );
                                  }
                                )}
                              </>
                            ) : (
                              <>
                                <tr>
                                  <td colSpan={3}>
                                    <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                      No Data found
                                    </div>
                                  </td>
                                </tr>
                              </>
                            )}
                          </>
                        )}
                      </>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
            {!loading && totalRecords > 0 ? (
              <Pagination
                totalRecords={totalRecords}
                currentPage={page}
                handleCurrentPage={handleCurrentPage}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
                handlePageLimit={handlePageLimit}
                pageLimit={pageLimit}
              />
            ) : (
              <></>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Notifications;
